import React, { useEffect, useRef } from 'react';

interface Bamboo {
  x: number;
  width: number;
  height: number;
  speed: number;
  offset: number;
  segments: number;
}

interface Shuriken {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  spin: number;
  spinSpeed: number;
}

interface Petal {
  x: number;
  y: number;
  size: number;
  speed: number;
  drift: number;
  rotation: number;
  rotationSpeed: number;
}

const NinjaBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reducedMotionQuery =
      typeof window !== 'undefined'
        ? window.matchMedia?.('(prefers-reduced-motion: reduce)')
        : undefined;
    let reducedMotion = reducedMotionQuery?.matches ?? false;

    let bamboos: Bamboo[] = [];
    let shurikens: Shuriken[] = [];
    let petals: Petal[] = [];
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initScene();
      if (reducedMotion) renderStatic();
    };

    function initScene() {
      const count = 7;
      bamboos = Array.from({ length: count }, (_, i) => ({
        x: (canvas.width / (count + 1)) * (i + 1) + (Math.random() - 0.5) * 60,
        width: Math.random() * 14 + 10,
        height: canvas.height * (0.55 + Math.random() * 0.4),
        speed: Math.random() * 0.01 + 0.004,
        offset: Math.random() * Math.PI * 2,
        segments: Math.floor(Math.random() * 4) + 5,
      }));

      shurikens = Array.from({ length: 4 }, () => spawnShuriken());

      petals = Array.from({ length: 18 }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 6 + 3,
        speed: Math.random() * 0.4 + 0.15,
        drift: (Math.random() - 0.5) * 0.4,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.04,
      }));
    }

    function spawnShuriken(): Shuriken {
      const fromLeft = Math.random() > 0.5;
      const speed = Math.random() * 4 + 4;
      return {
        x: fromLeft ? -40 : canvas.width + 40,
        y: Math.random() * canvas.height * 0.6,
        vx: fromLeft ? speed : -speed,
        vy: Math.random() * 1.5 + 0.5,
        size: Math.random() * 10 + 12,
        spin: Math.random() * Math.PI,
        spinSpeed: (fromLeft ? 1 : -1) * (Math.random() * 0.3 + 0.3),
      };
    }

    function drawSky() {
      const sky = ctx.createLinearGradient(0, 0, 0, canvas.height);
      sky.addColorStop(0, '#070910');
      sky.addColorStop(0.6, '#0c1018');
      sky.addColorStop(1, '#11151f');
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    function drawMoon() {
      const mx = canvas.width * 0.78;
      const my = canvas.height * 0.22;
      const r = Math.min(canvas.width, canvas.height) * 0.16;

      const halo = ctx.createRadialGradient(mx, my, 0, mx, my, r * 2.4);
      halo.addColorStop(0, 'rgba(185, 28, 28, 0.45)');
      halo.addColorStop(1, 'rgba(185, 28, 28, 0)');
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(mx, my, r * 2.4, 0, Math.PI * 2);
      ctx.fill();

      const disc = ctx.createRadialGradient(mx - r * 0.3, my - r * 0.3, r * 0.2, mx, my, r);
      disc.addColorStop(0, '#fca5a5');
      disc.addColorStop(0.5, '#ef4444');
      disc.addColorStop(1, '#991b1b');
      ctx.fillStyle = disc;
      ctx.beginPath();
      ctx.arc(mx, my, r, 0, Math.PI * 2);
      ctx.fill();
    }

    function drawBamboo(b: Bamboo) {
      const sway = Math.sin(time * b.speed * 30 + b.offset) * 14;
      ctx.strokeStyle = 'rgba(8, 11, 16, 0.92)';
      ctx.lineWidth = b.width;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(b.x, canvas.height);
      ctx.quadraticCurveTo(
        b.x + sway * 0.5,
        canvas.height - b.height * 0.5,
        b.x + sway,
        canvas.height - b.height
      );
      ctx.stroke();

      // Node rings
      ctx.strokeStyle = 'rgba(40, 45, 55, 0.8)';
      ctx.lineWidth = 2;
      for (let i = 1; i < b.segments; i++) {
        const t = i / b.segments;
        const ny = canvas.height - b.height * t;
        const nx = b.x + sway * t;
        ctx.beginPath();
        ctx.moveTo(nx - b.width / 2, ny);
        ctx.lineTo(nx + b.width / 2, ny);
        ctx.stroke();
      }
    }

    function drawShurikenShape(size: number) {
      ctx.fillStyle = 'rgba(203, 213, 225, 0.92)';
      ctx.beginPath();
      for (let i = 0; i < 4; i++) {
        const a = (Math.PI / 2) * i;
        const px = Math.cos(a) * size;
        const py = Math.sin(a) * size;
        if (i === 0) {
          ctx.moveTo(px, py);
        } else {
          ctx.lineTo(px, py);
        }
        ctx.lineTo(
          Math.cos(a + Math.PI / 4) * size * 0.3,
          Math.sin(a + Math.PI / 4) * size * 0.3
        );
      }
      ctx.closePath();
      ctx.fill();
      // Center hole
      ctx.fillStyle = '#11151f';
      ctx.beginPath();
      ctx.arc(0, 0, size * 0.18, 0, Math.PI * 2);
      ctx.fill();
    }

    function drawShuriken(s: Shuriken) {
      ctx.save();
      ctx.translate(s.x, s.y);
      ctx.rotate(s.spin);
      drawShurikenShape(s.size);
      ctx.restore();
    }

    function updateShuriken(s: Shuriken) {
      s.x += s.vx;
      s.y += s.vy;
      s.spin += s.spinSpeed;
      if (s.x < -60 || s.x > canvas.width + 60 || s.y > canvas.height + 60) {
        Object.assign(s, spawnShuriken());
      }
    }

    function drawPetal(p: Petal) {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      ctx.fillStyle = 'rgba(127, 29, 29, 0.55)';
      ctx.beginPath();
      ctx.ellipse(0, 0, p.size, p.size * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    function updatePetal(p: Petal) {
      p.y += p.speed;
      p.x += p.drift + Math.sin(time + p.y * 0.01) * 0.3;
      p.rotation += p.rotationSpeed;
      if (p.y > canvas.height) {
        p.y = -10;
        p.x = Math.random() * canvas.width;
      }
    }

    function renderStatic() {
      drawSky();
      drawMoon();
      bamboos.forEach(drawBamboo);
      shurikens.forEach(drawShuriken);
      petals.forEach(drawPetal);
    }

    function animate() {
      if (reducedMotion) return;
      time += 0.01;
      drawSky();
      drawMoon();
      bamboos.forEach(drawBamboo);
      petals.forEach((p) => {
        updatePetal(p);
        drawPetal(p);
      });
      shurikens.forEach((s) => {
        updateShuriken(s);
        drawShuriken(s);
      });
      animationFrameId.current = requestAnimationFrame(animate);
    }

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const handleReducedMotionChange = (event: MediaQueryListEvent) => {
      reducedMotion = event.matches;
      cancelAnimationFrame(animationFrameId.current);
      if (reducedMotion) {
        renderStatic();
      } else {
        animate();
      }
    };

    reducedMotionQuery?.addEventListener?.('change', handleReducedMotionChange);

    if (reducedMotion) {
      renderStatic();
    } else {
      animate();
    }

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      reducedMotionQuery?.removeEventListener?.('change', handleReducedMotionChange);
      cancelAnimationFrame(animationFrameId.current);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />;
};

export default NinjaBackground;
